import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiService, ColumnFilter, EnvironmentModel, UserProfileProperty} from '@cas-hub/acabim-common-client';
import {HttpClient} from '@angular/common/http';
import {Contact} from '@cas-hub/acabim-common-client/lib/_models/user.model';
import {map} from 'rxjs/operators';
import {ContactParcel} from '../_models/contact-details.model';
import {FormData} from '../_models/contact-details.model';

export class ContactFilter {
    public static readonly STATUS_FILTER: ColumnFilter = {
        columnData: {displayName: 'Status', objectPropertyName: 'status'},
        filterOptions: [{label: 'inactive', value: '0'}, {label: 'active', value: '1'}],
        selectedValue: '1',
        displayLabel: false
    };

    public static getFilters(): ColumnFilter[] {
        return [ContactFilter.STATUS_FILTER];
    }
}

@Injectable({
    providedIn: 'root'
})
export class ContactSetterService extends ApiService {

    constructor(protected client: HttpClient,
                protected environment: EnvironmentModel) {
        super(client, environment);
    }

    protected controller = 'contact';


    public addContactAlt(gatheredData: ContactParcel, contactId?: string): Observable<Contact> {
        console.log(JSON.stringify(gatheredData));
        return this.client.post(this.url(contactId ?? ''), gatheredData, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => item as Contact));
    }

    public addContact(contactType: number, properties: [UserProfileProperty], organisation: [UserProfileProperty], userId?: string, contactId?: number): Observable<Contact> {
        const contactProperties: { type: string; label: string; value: string; default_for_type: number; }[] = [];
        const organisationProperties: { type: string; label: string; value: string; default_for_type: number; }[] = [];
        if (properties.length > 0) {
            properties.forEach(p => {
                const property = {
                    type: p.type,
                    label: p.label,
                    value: p.value,
                    default_for_type: p.default_for_type
                };
                contactProperties.push(property);
            });
        }
        if (organisation.length > 0) {
            organisation.forEach(o => {
                const property = {
                    type: o.type,
                    label: o.label,
                    value: o.value,
                    default_for_type: o.default_for_type
                };
                organisationProperties.push(property);
            });
        }
        const body = {
            user_id: userId ? userId : null,
            contact_type: contactType,
            profile_properties: contactProperties,
            organisation_properties: organisationProperties,
        };

        console.log(JSON.stringify(body));
        return this.client.post(this.url('/' + contactId), body, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => item as Contact));
    }

    public deleteContact(contactId: number) {
        return this.client.delete(this.url(String(contactId)), {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => item as Contact));

    }

    public assignRole(role: string, contacts: number[]) {
        return this.client.post(
            this.url(`contact-role-type/${role}/contacts`, true),
            contacts,
            {
                headers: this.defaultHeaders,
                responseType: 'json'
            }
        ).pipe(map(item => item as {status: string, data: number}));

    }

    public updateProperty(contactId: number, propertyID: number, body: FormData) {
        const requestBody = {
            type: body.label,
            label: '',
            value: body.data,
            default_for_type: 1
        };
        return this.client.post(this.url(String(contactId) + '/profile/' + String(propertyID)), requestBody, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));

    }

    public updateOrganisationProperty(contactId: number, propertyID: number, body: FormData) {
        const requestBody = {
            type: body.label,
            label: '',
            value: body.data,
            default_for_type: 1
        };
        return this.client.post(this.url(String(contactId) + '/organisation/' + String(propertyID)), requestBody, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));
    }

    public updateContactType(contactId: number, contactTypeId: number) {
        return this.client.post(this.url(String(contactId) + '/type/' + String(contactTypeId)), undefined, {
            headers: this.defaultHeaders,
            responseType: 'json'
        });
    }

    public getContactProperties(contactId: string): Observable<Contact> {
        return this.client.get(this.url(String(contactId)), {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));
    }

    public makeManager(contactId: number) {
        return this.client.post(this.url(String(contactId) + '/manager'), {}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));
    }

    public removeManager(contactId: number) {
        console.log(this.url(String(contactId) + '/manager'));
        return this.client.delete(this.url(String(contactId) + '/manager')).pipe(map(item => (item as Contact)));
    }

    public getPreferredContactMethod(contactId: number) {
        return this.client.get(this.url(String(contactId) + '/preferred')).pipe(map(item => (item as UserProfileProperty)));
    }

    public setPreferredContactMethod(contactId: number, profilePropertyId: number) {
        return this.client.post(this.url(String(contactId) + '/preferred/' + String(profilePropertyId)), {}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as UserProfileProperty)));
    }

    public removePreferredContactMethod(contactId: number, profilePropertyId: number) {
        return this.client.delete(this.url(String(contactId) + '/preferred/' + String(profilePropertyId))).pipe(map(item => (item as UserProfileProperty)));
    }

    public setContactType(contactId: number, contactTypeId: number) {
        return this.client.post(this.url(String(contactId) + '/type/' + String(contactTypeId)), {}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));
    }

    public toggleContactStatus(contactId: number) {
        return this.client.post(this.url(String(contactId) + '/status'), {}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));
    }

    public toggleContactLegal(contactId: number) {
        return this.client.post(this.url(String(contactId) + '/legal-representative'), {}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));
    }

    public addProperty(contactId: number, body: FormData) {
        const requestBody = {
            type: body.label,
            label: '',
            value: body.data,
            default_for_type: 1
        };
        return this.client.post(this.url(String(contactId) + '/profile'), requestBody, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => (item as Contact)));
    }

    public upload(contactId: string, fileGUIDs: string[]) {
        return this.client.post(this.url('file-usage/contact/' + contactId, true), fileGUIDs, {
            headers: this.defaultHeaders,
            responseType: 'json'
        });
    }

    public getFileGUIDs(contactId: string) {
        return this.client.get(this.url('file-usage/contact/' + contactId, true), {
            headers: this.defaultHeaders,
            responseType: 'json'
        });
    }

    public removeFileGuids(contactId: string, fileGUIDs: string[]) {
        const option = {
            headers: this.defaultHeaders,
            body: fileGUIDs
        };
        return this.client.delete(this.url('file-usage/contact/' + contactId, true), option);
    }

}
